import {
  CustomToolHttpMethod,
  defaultCustomToolTimeoutMs,
  extractCustomToolPathPlaceholders,
  findCustomToolUrlIssue,
  type CustomToolPathParameter,
  type CustomToolSettings,
} from "@repo/app-types/entities/tools/custom";
import { isNotNullNotUndefined } from "@repo/app-types/helpers/utilities";
import {
  CustomToolForm,
  type CustomToolFormSettings,
} from "../components/customToolForm.js";
import { keyValuePairsToRecord, recordToKeyValuePairs } from "../utils.js";
import type { ToolFormConfig } from "./baseFormConfig.js";
import {
  buildToolMock,
  initializeToolMockFormSettings,
} from "./toolMockResponse.js";

export const customToolFormConfig: ToolFormConfig<
  CustomToolSettings,
  CustomToolFormSettings
> = {
  initialize: (settings) => ({
    method: settings?.method ?? CustomToolHttpMethod.POST,
    url: settings?.url ?? "",
    urlError: undefined,
    timeout: (settings?.timeout_ms ?? defaultCustomToolTimeoutMs).toString(),
    timeoutError: undefined,
    headers: recordToKeyValuePairs(settings?.headers),
    queryParams: recordToKeyValuePairs(settings?.query_params),
    parameters: settings?.parameters ?? [],
    pathParameters: settings?.path_parameters ?? [],
    endsCall: settings?.ends_call ?? false,
    ...initializeToolMockFormSettings(settings?.mock),
  }),
  validate: (settings) => settings.url.trim() !== "",
  save: (settings) => {
    const headersRecord = keyValuePairsToRecord(settings.headers);
    const queryParamsRecord = keyValuePairsToRecord(settings.queryParams);
    const filteredParams = settings.parameters.filter(
      (p) => p.name.trim() !== "",
    );

    const url = settings.url.trim();

    // Guidance is kept per placeholder in the form, so drop anything left over
    // from a placeholder the user has since removed from the URL.
    const pathParameters = extractCustomToolPathPlaceholders(url).reduce(
      (acc, placeholder) => {
        const description = settings.pathParameters
          .find((pathParameter) => pathParameter.name === placeholder)
          ?.description.trim();

        if (description) {
          acc.push({ name: placeholder, description });
        }

        return acc;
      },
      [] as CustomToolPathParameter[],
    );

    const urlIssue = findCustomToolUrlIssue({
      url,
      parameters: filteredParams,
      path_parameters: pathParameters,
    });

    if (urlIssue) {
      return { isValid: false, formSettings: { urlError: urlIssue } };
    }

    // Make sure timeout is a valid number
    const timeoutValue =
      settings.timeout.trim() !== ""
        ? Number(settings.timeout.trim())
        : undefined;

    if (isNotNullNotUndefined(timeoutValue) && isNaN(timeoutValue)) {
      return {
        isValid: false,
        formSettings: {
          timeoutError: "Timeout must be a valid number",
        },
      };
    }

    const mockResult = buildToolMock(settings);
    if (!mockResult.isValid) {
      return {
        isValid: false,
        formSettings: { mockResponseError: mockResult.mockResponseError },
      };
    }

    return {
      isValid: true,
      settings: {
        method: settings.method,
        url,
        timeout_ms: timeoutValue,
        headers: headersRecord,
        query_params: queryParamsRecord,
        parameters: filteredParams.length > 0 ? filteredParams : undefined,
        path_parameters: pathParameters.length > 0 ? pathParameters : undefined,
        ends_call: settings.endsCall || undefined,
        mock: mockResult.mock,
      },
    };
  },
  readProposedSettings: (settings) => ({
    ...(settings.method ? { method: settings.method } : {}),
    // The URL's check covers the placeholders and parameters too.
    ...(settings.url !== undefined ||
    settings.path_parameters !== undefined ||
    settings.parameters !== undefined
      ? { urlError: undefined }
      : {}),
    ...(settings.url === undefined ? {} : { url: settings.url }),
    ...(settings.path_parameters
      ? { pathParameters: settings.path_parameters }
      : {}),
    ...(settings.parameters ? { parameters: settings.parameters } : {}),
    ...(settings.timeout_ms === undefined
      ? {}
      : { timeout: settings.timeout_ms.toString(), timeoutError: undefined }),
    ...(settings.ends_call === undefined
      ? {}
      : { endsCall: settings.ends_call }),
    ...(settings.mock ? initializeToolMockFormSettings(settings.mock) : {}),
  }),
  Form: CustomToolForm,
};
